import type { SheetCounts, SheetRow, SheetStatus } from '../../api/types';

/** Sheets the reviewer has to look at (the design's review queue). */
export const REVIEW_STATUSES: SheetStatus[] = ['needs_review', 'duplicate', 'error'];

export function isReviewable(s: SheetStatus): boolean {
  return REVIEW_STATUSES.includes(s);
}

/** The status counts the backend sends, recomputed over a subset of sheets (a section scope). */
export function countSheets(scans: SheetRow[]): SheetCounts {
  const c: SheetCounts = {
    queued: 0, reading: 0, matched: 0, needs_review: 0, duplicate: 0, error: 0, confirmed: 0, total: 0,
  };
  for (const s of scans) {
    if (s.status === 'discarded') continue;
    c[s.status] += 1;
    c.total += 1;
  }
  return c;
}

export interface RosterStudent { id: number; name: string; section: string | null; roll: number | null }

/** Students of the exam's audience with no non-discarded sheet matched to them. */
export function missingStudents(roster: RosterStudent[], scans: SheetRow[]): RosterStudent[] {
  const got = new Set<number>();
  for (const s of scans) if (s.status !== 'discarded' && s.student) got.add(s.student.id);
  return roster.filter((r) => !got.has(r.id));
}

/** Rows the reader flagged: two bubbles (`multi:1,4`) or a faint mark (`low_confidence:2` or confidence < threshold). */
export function flaggedRows(
  warnings: string[],
  rowConfidence: Record<string, number>,
  nRows: number,
  lowThreshold: number,
): Map<number, 'multi' | 'low'> {
  const out = new Map<number, 'multi' | 'low'>();
  const codes = (prefix: string) => warnings
    .filter((w) => w.startsWith(prefix))
    .flatMap((w) => w.slice(prefix.length).split(',').map((p) => Number(p.trim())))
    .filter((n) => Number.isFinite(n) && n >= 1 && n <= nRows);
  for (const n of codes('low_confidence:')) out.set(n, 'low');
  for (let r = 1; r <= nRows; r++) if ((rowConfidence[String(r)] ?? 1) < lowThreshold) out.set(r, 'low');
  for (const n of codes('multi:')) out.set(n, 'multi');
  return out;
}
